import React, { useEffect, useState, useMemo, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import SearchableSelect from '../components/SearchableSelect';

// Register the datalabels plugin globally
Chart.register(ChartDataLabels);

export default function TrendsDashboard() {
  const [selectedRangePreset, setSelectedRangePreset] = useState('all_time');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [oldestSystemDate, setOldestSystemDate] = useState('2025-01-01');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedGroupBy, setSelectedGroupBy] = useState('monthly');
  const [selectedLayout, setSelectedLayout] = useState('list'); // 'list' or 'grid'
  const [graphOrder, setGraphOrder] = useState(['jobs', 'leads', 'scored', 'total_tasks', 'tasks_breakdown']);
  const [selectedStandaloneStatus, setSelectedStandaloneStatus] = useState('');
  const [jobRoles, setJobRoles] = useState([]);
  const [chartData, setChartData] = useState({
    periods: [],
    jobs: [],
    leads: [],
    scored_jobs: [],
    tasks: {},
    distinct_scored_jobs: [],
    distinct_jobs_with_tasks: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Special Graphs toggle
  const [showSpecialGraphs, setShowSpecialGraphs] = useState(false);

  // Merge Graphs state
  const [mergedGraphs, setMergedGraphs] = useState([]); // [{id, name, sources:[graphId,...]}]
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeModalName, setMergeModalName] = useState('');
  const [mergeModalSources, setMergeModalSources] = useState([]);
  const [editingMergeId, setEditingMergeId] = useState(null); // null = create new

  // Per-graph data label settings: visible flag + label color + line color
  const [dataLabelSettings, setDataLabelSettings] = useState({
    jobs:              { visible: true, color: '#6366f1', lineColor: '#6366f1' },
    leads:             { visible: true, color: '#10b981', lineColor: '#10b981' },
    scored:            { visible: true, color: '#f59e0b', lineColor: '#f59e0b' },
    total_tasks:       { visible: true, color: '#8b5cf6', lineColor: '#8b5cf6' },
    tasks_breakdown:   { visible: true, color: '#1e293b' },
    specific_status:   { visible: true, color: '#6366f1', lineColor: '#6366f1' },
    distinct_scored:   { visible: true, color: '#06b6d4', lineColor: '#06b6d4' },
    distinct_tasks:    { visible: true, color: '#f43f5e', lineColor: '#f43f5e' },
  });

  const updateDataLabelSetting = (graphId, key, value) => {
    setDataLabelSettings(prev => ({
      ...prev,
      [graphId]: { ...prev[graphId], [key]: value }
    }));
  };

  // References for Canvas elements
  const jobsChartRef = useRef(null);
  const leadsChartRef = useRef(null);
  const scoredChartRef = useRef(null);
  const totalTasksChartRef = useRef(null);
  const tasksChartRef = useRef(null);
  const specificStatusChartRef = useRef(null);
  const distinctScoredChartRef = useRef(null);
  const distinctTasksChartRef = useRef(null);

  // References for Chart.js instances
  const jobsChartInstance = useRef(null);
  const leadsChartInstance = useRef(null);
  const scoredChartInstance = useRef(null);
  const totalTasksChartInstance = useRef(null);
  const tasksChartInstance = useRef(null);
  const specificStatusChartInstance = useRef(null);
  const distinctScoredChartInstance = useRef(null);
  const distinctTasksChartInstance = useRef(null);

  // Dynamic refs for merged graphs
  const mergedChartRefs = useRef({});      // { mergeId: { current: canvasElement } }
  const mergedChartInstances = useRef({}); // { mergeId: Chart instance }

  // Automatically append/remove specific_status to graphOrder when state changes
  useEffect(() => {
    if (selectedStandaloneStatus) {
      setGraphOrder(g => g.includes('specific_status') ? g : [...g, 'specific_status']);
    } else {
      setGraphOrder(g => g.filter(id => id !== 'specific_status'));
    }
  }, [selectedStandaloneStatus]);

  // Append/remove special graphs from graphOrder when toggle changes
  useEffect(() => {
    if (showSpecialGraphs) {
      setGraphOrder(g => {
        const next = [...g];
        if (!next.includes('distinct_scored')) next.push('distinct_scored');
        if (!next.includes('distinct_tasks')) next.push('distinct_tasks');
        return next;
      });
    } else {
      setGraphOrder(g => g.filter(id => id !== 'distinct_scored' && id !== 'distinct_tasks'));
    }
  }, [showSpecialGraphs]);

  // Fetch dropdown job roles & oldest system date on mount
  useEffect(() => {
    const controller = new AbortController();
    // 1. Fetch oldest system date
    axios.get('/api/trends-dashboard-data/?get_oldest_date=true', { signal: controller.signal })
      .then(res => {
        if (res.data && res.data.oldest_date) {
          setOldestSystemDate(res.data.oldest_date);
        }
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch oldest date', err);
        }
      });

    // 2. Fetch job roles
    axios.get('/api/v1/jobroles/?page_size=500', { signal: controller.signal })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
        setJobRoles(data);
      })
      .catch(err => {
        if (!axios.isCancel(err)) {
          console.error('Failed to fetch job roles', err);
        }
      });

    return () => {
      controller.abort();
    };
  }, []);

  // Compute calculated start and end dates based on preset
  const dateRangeParams = useMemo(() => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    const todayStr = formatDate(today);

    if (selectedRangePreset === 'all_time') {
      return { start_date: oldestSystemDate, end_date: todayStr };
    }
    if (selectedRangePreset === 'last_30_days') {
      const start = new Date();
      start.setDate(today.getDate() - 30);
      return { start_date: formatDate(start), end_date: todayStr };
    }
    if (selectedRangePreset === 'last_3_months') {
      const start = new Date();
      start.setMonth(today.getMonth() - 3);
      return { start_date: formatDate(start), end_date: todayStr };
    }
    if (selectedRangePreset === 'last_6_months') {
      const start = new Date();
      start.setMonth(today.getMonth() - 6);
      return { start_date: formatDate(start), end_date: todayStr };
    }
    if (selectedRangePreset === 'last_12_months') {
      const start = new Date();
      start.setMonth(today.getMonth() - 12);
      return { start_date: formatDate(start), end_date: todayStr };
    }
    if (selectedRangePreset === 'custom') {
      return { start_date: customStartDate || oldestSystemDate, end_date: customEndDate || todayStr };
    }
    return { start_date: oldestSystemDate, end_date: todayStr };
  }, [selectedRangePreset, oldestSystemDate, customStartDate, customEndDate]);

  // Auto-switch default grouping based on preset range to keep chart legible
  useEffect(() => {
    if (selectedRangePreset === 'last_30_days') {
      setSelectedGroupBy('daily');
    } else if (selectedRangePreset === 'last_3_months' || selectedRangePreset === 'last_6_months') {
      setSelectedGroupBy('monthly');
    }
  }, [selectedRangePreset]);

  // Fetch chart data on filter change — debounced 300ms to avoid burst requests
  useEffect(() => {
    const controller = new AbortController();
    const fetchTrends = async () => {
      setLoading(true);
      setError(null);
      try {
        const roleParam = selectedRole ? `&role_id=${encodeURIComponent(selectedRole)}` : '';
        const res = await axios.get(
          `/api/trends-dashboard-data/?start_date=${dateRangeParams.start_date}&end_date=${dateRangeParams.end_date}&group_by=${selectedGroupBy}${roleParam}`,
          { signal: controller.signal }
        );
        setChartData(res.data || { periods: [], jobs: [], leads: [], scored_jobs: [], tasks: {} });
      } catch (err) {
        if (axios.isCancel(err)) {
          return;
        }
        console.error('Failed to fetch trends data', err);
        setError('Failed to load trends dashboard data. Please try again.');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };
    // 300ms debounce — cancels if filters change again quickly
    const timer = setTimeout(fetchTrends, 300);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [dateRangeParams, selectedRole, selectedGroupBy]);

  // Compute total task count for each period
  const totalTasksData = useMemo(() => {
    const periodsCount = (chartData.periods || []).length;
    if (periodsCount === 0) return [];
    const total = Array(periodsCount).fill(0);
    Object.keys(chartData.tasks || {}).forEach(statusKey => {
      const statusData = chartData.tasks[statusKey] || [];
      for (let i = 0; i < periodsCount; i++) {
        total[i] += (statusData[i] || 0);
      }
    });
    return total;
  }, [chartData]);

  // Compute selected standalone task status trend data
  const standaloneStatusData = useMemo(() => {
    const periodsCount = (chartData.periods || []).length;
    if (periodsCount === 0 || !selectedStandaloneStatus) return [];
    return chartData.tasks[selectedStandaloneStatus] || Array(periodsCount).fill(0);
  }, [chartData, selectedStandaloneStatus]);

  // Reordering handler
  const moveGraph = (id, direction) => {
    const index = graphOrder.indexOf(id);
    if (index === -1) return;
    const newOrder = [...graphOrder];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= graphOrder.length) return;

    // Swap positions
    const temp = newOrder[index];
    newOrder[index] = newOrder[targetIndex];
    newOrder[targetIndex] = temp;
    setGraphOrder(newOrder);
  };

  // Resolve all individual lines (name, id, default color) for a merged graph (supports nested merges)
  const resolveMergedLines = useMemo(() => {
    const STATUS_MAP = {
      PENDING: '#f59e0b',
      STARTED: '#3b82f6',
      IN_PROGRESS: '#06b6d4',
      COMPLETED: '#10b981',
      'AWAITING EMAIL': '#8b5cf6',
      ALREADY_APPLIED: '#14b8a6',
      NOT_RELEVANT: '#ef4444',
      JOB_NOT_FOUND: '#f97316',
      NEED_OTP: '#ec4899',
    };
    const TASK_STATUSES = [
      { key: 'PENDING',        label: 'Pending',        color: 'rgba(245,158,11,1)' },
      { key: 'STARTED',        label: 'Started',        color: 'rgba(59,130,246,1)' },
      { key: 'IN_PROGRESS',    label: 'In Progress',    color: 'rgba(6,182,212,1)' },
      { key: 'COMPLETED',      label: 'Completed',      color: 'rgba(16,185,129,1)' },
      { key: 'AWAITING EMAIL', label: 'Awaiting Email', color: 'rgba(139,92,246,1)' },
      { key: 'ALREADY_APPLIED',label: 'Already Applied',color: 'rgba(20,184,166,1)' },
      { key: 'NOT_RELEVANT',   label: 'Not Relevant',   color: 'rgba(239,68,68,1)' },
      { key: 'JOB_NOT_FOUND',  label: 'Job Not Found',  color: 'rgba(249,115,22,1)' },
      { key: 'NEED_OTP',       label: 'Need OTP',       color: 'rgba(236,72,153,1)' },
    ];

    const resolve = (sources, visited = new Set()) => {
      const lines = [];
      sources.forEach(srcId => {
        if (visited.has(srcId)) return;
        visited.add(srcId);
        if (srcId === 'jobs') {
          lines.push({ id: 'jobs', label: 'Jobs Uploaded', defaultColor: '#6366f1' });
        } else if (srcId === 'leads') {
          lines.push({ id: 'leads', label: 'Leads Created', defaultColor: '#10b981' });
        } else if (srcId === 'scored') {
          lines.push({ id: 'scored', label: 'Jobs Scored', defaultColor: '#f59e0b' });
        } else if (srcId === 'total_tasks') {
          lines.push({ id: 'total_tasks', label: 'Total Tasks', defaultColor: '#8b5cf6' });
        } else if (srcId === 'distinct_scored') {
          lines.push({ id: 'distinct_scored', label: 'Distinct Jobs Scored', defaultColor: '#06b6d4' });
        } else if (srcId === 'distinct_tasks') {
          lines.push({ id: 'distinct_tasks', label: 'Distinct Jobs w/ Tasks', defaultColor: '#f43f5e' });
        } else if (srcId === 'specific_status' && selectedStandaloneStatus) {
          lines.push({ id: 'specific_status', label: `${selectedStandaloneStatus} Tasks`, defaultColor: STATUS_MAP[selectedStandaloneStatus] || '#6366f1' });
        } else if (srcId === 'tasks_breakdown') {
          TASK_STATUSES.forEach(st => {
            lines.push({ id: `tasks_breakdown_${st.key}`, label: st.label, defaultColor: st.color });
          });
        } else if (srcId.startsWith('merged_')) {
          const mg = mergedGraphs.find(m => m.id === srcId);
          if (mg) {
            lines.push(...resolve(mg.sources, new Set(visited)));
          }
        }
      });
      return lines;
    };

    return (sources) => resolve(sources);
  }, [selectedStandaloneStatus, mergedGraphs]);

  // Resolve a list of source graph IDs into Chart.js dataset arrays (supports nested merges)
  const getMergedDatasets = useMemo(() => {
    const TASK_STATUSES = [
      { key: 'PENDING',        label: 'Pending',        color: 'rgba(245,158,11,1)' },
      { key: 'STARTED',        label: 'Started',        color: 'rgba(59,130,246,1)' },
      { key: 'IN_PROGRESS',    label: 'In Progress',    color: 'rgba(6,182,212,1)' },
      { key: 'COMPLETED',      label: 'Completed',      color: 'rgba(16,185,129,1)' },
      { key: 'AWAITING EMAIL', label: 'Awaiting Email', color: 'rgba(139,92,246,1)' },
      { key: 'ALREADY_APPLIED',label: 'Already Applied',color: 'rgba(20,184,166,1)' },
      { key: 'NOT_RELEVANT',   label: 'Not Relevant',   color: 'rgba(239,68,68,1)' },
      { key: 'JOB_NOT_FOUND',  label: 'Job Not Found',  color: 'rgba(249,115,22,1)' },
      { key: 'NEED_OTP',       label: 'Need OTP',       color: 'rgba(236,72,153,1)' },
    ];
    const STATUS_MAP = { PENDING:'#f59e0b',STARTED:'#3b82f6',IN_PROGRESS:'#06b6d4',COMPLETED:'#10b981','AWAITING EMAIL':'#8b5cf6',ALREADY_APPLIED:'#14b8a6',NOT_RELEVANT:'#ef4444',JOB_NOT_FOUND:'#f97316',NEED_OTP:'#ec4899' };
    const nPeriods = (chartData.periods || []).length;

    const makeLine = (label, data, color, mgId, lineKey) => {
      const mgCfg = dataLabelSettings[mgId] || { visible: true, color: '#6366f1' };
      const showLabels = mgCfg.visible !== false;
      return {
        label, data, borderColor: color, borderWidth: 2.5, fill: false, tension: 0.35,
        pointBackgroundColor: color, pointBorderColor: '#fff', pointBorderWidth: 1.5,
        pointRadius: 4, pointHoverRadius: 6,
        datalabels: showLabels ? {
          display: true,
          align: 'top',
          anchor: 'end',
          offset: 3,
          color: mgCfg.color || color,
          font: { family: 'Outfit, sans-serif', size: 9, weight: '700' },
          formatter: (value) => (value === 0 ? '' : value)
        } : { display: false }
      };
    };

    // Resolve sources recursively; visited guards against cycles
    const resolve = (sources, parentMgId, visited = new Set()) => {
      const datasets = [];
      sources.forEach(srcId => {
        if (visited.has(srcId)) return;
        visited.add(srcId);

        const getLineColor = (id, defaultColor) => {
          if (parentMgId && dataLabelSettings[parentMgId]?.lineColors?.[id]) {
            return dataLabelSettings[parentMgId].lineColors[id];
          }
          if (dataLabelSettings[id]?.lineColor) {
            return dataLabelSettings[id].lineColor;
          }
          return defaultColor;
        };

        if (srcId === 'jobs') {
          const color = getLineColor('jobs', '#6366f1');
          datasets.push(makeLine('Jobs Uploaded', chartData.jobs || [], color, parentMgId, 'jobs'));
        } else if (srcId === 'leads') {
          const color = getLineColor('leads', '#10b981');
          datasets.push(makeLine('Leads Created', chartData.leads || [], color, parentMgId, 'leads'));
        } else if (srcId === 'scored') {
          const color = getLineColor('scored', '#f59e0b');
          datasets.push(makeLine('Jobs Scored', chartData.scored_jobs || [], color, parentMgId, 'scored'));
        } else if (srcId === 'total_tasks') {
          const color = getLineColor('total_tasks', '#8b5cf6');
          const total = Array(nPeriods).fill(0);
          Object.values(chartData.tasks || {}).forEach(arr => {
            for (let i = 0; i < nPeriods; i++) total[i] += (arr[i] || 0);
          });
          datasets.push(makeLine('Total Tasks', total, color, parentMgId, 'total_tasks'));
        } else if (srcId === 'distinct_scored') {
          const color = getLineColor('distinct_scored', '#06b6d4');
          datasets.push(makeLine('Distinct Jobs Scored', chartData.distinct_scored_jobs || [], color, parentMgId, 'distinct_scored'));
        } else if (srcId === 'distinct_tasks') {
          const color = getLineColor('distinct_tasks', '#f43f5e');
          datasets.push(makeLine('Distinct Jobs w/ Tasks', chartData.distinct_jobs_with_tasks || [], color, parentMgId, 'distinct_tasks'));
        } else if (srcId === 'specific_status' && selectedStandaloneStatus) {
          const defaultColor = STATUS_MAP[selectedStandaloneStatus] || '#6366f1';
          const color = getLineColor('specific_status', defaultColor);
          datasets.push(makeLine(`${selectedStandaloneStatus} Tasks`, chartData.tasks[selectedStandaloneStatus] || [], color, parentMgId, 'specific_status'));
        } else if (srcId === 'tasks_breakdown') {
          TASK_STATUSES.forEach(st => {
            const key = `tasks_breakdown_${st.key}`;
            const color = getLineColor(key, st.color);
            datasets.push(makeLine(st.label, chartData.tasks[st.key] || [], color, parentMgId, key));
          });
        } else if (srcId.startsWith('merged_')) {
          const mg = mergedGraphs.find(m => m.id === srcId);
          if (mg) {
            // Recurse with the nested merged graph's ID
            datasets.push(...resolve(mg.sources, srcId, new Set(visited)));
          }
        }
      });
      return datasets;
    };

    return (sources, parentMgId) => resolve(sources, parentMgId);
  }, [chartData, selectedStandaloneStatus, mergedGraphs, totalTasksData, dataLabelSettings]);

  // Instantiate / update Chart.js line charts
  useEffect(() => {
    const hasData = (chartData.periods || []).length > 0;
    if (loading || !hasData) return;

    // Destroy existing chart instances to prevent canvas reuse errors
    if (jobsChartInstance.current) jobsChartInstance.current.destroy();
    if (leadsChartInstance.current) leadsChartInstance.current.destroy();
    if (scoredChartInstance.current) scoredChartInstance.current.destroy();
    if (totalTasksChartInstance.current) totalTasksChartInstance.current.destroy();
    if (tasksChartInstance.current) tasksChartInstance.current.destroy();
    if (specificStatusChartInstance.current) specificStatusChartInstance.current.destroy();
    if (distinctScoredChartInstance.current) distinctScoredChartInstance.current.destroy();
    if (distinctTasksChartInstance.current) distinctTasksChartInstance.current.destroy();
    // Destroy all merged graph instances
    Object.keys(mergedChartInstances.current).forEach(mid => {
      if (mergedChartInstances.current[mid]) mergedChartInstances.current[mid].destroy();
    });
    mergedChartInstances.current = {};

    // Map raw database period keys to human-readable strings
    const periodLabels = chartData.periods.map(pStr => {
      if (!pStr) return '';
      if (selectedGroupBy === 'yearly') {
        return pStr;
      }
      if (selectedGroupBy === 'quarterly') {
        const [year, quarter] = pStr.split('-Q');
        return `Q${quarter} ${year}`;
      }
      if (selectedGroupBy === 'monthly') {
        const [year, month] = pStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
      if (selectedGroupBy === 'daily') {
        const [year, month, day] = pStr.split('-');
        const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      return pStr;
    });

    const commonTicksConfig = {
      font: { family: 'Outfit, sans-serif', size: 11, weight: '600' },
      color: '#64748b'
    };

    // Common datalabels config for single-line charts
    const getSingleLineDatalabels = (graphId, fallbackColor) => {
      const cfg = dataLabelSettings[graphId] || {};
      if (!cfg.visible) return { display: false };
      const labelColor = cfg.color || fallbackColor;
      return {
        display: true,
        align: 'top',
        anchor: 'end',
        offset: 4,
        color: labelColor,
        font: { family: 'Outfit, sans-serif', size: 10, weight: '700' },
        formatter: (value) => (value === 0 ? '' : value)
      };
    };

    // Shared layout options helper
    const getCommonOptions = (showLegend = false, datalabelsConfig = null) => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: {
            font: { family: 'Outfit, sans-serif', size: 11, weight: '600' },
            boxWidth: 12,
            padding: 16
          }
        },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.9)',
          titleFont: { size: 13, weight: 'bold', family: 'Outfit, sans-serif' },
          bodyFont: { size: 12, family: 'Outfit, sans-serif' },
          padding: 12,
          cornerRadius: 10
        },
        datalabels: datalabelsConfig || { display: false }
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            ...commonTicksConfig,
            autoSkip: false,
            maxRotation: 45,
            minRotation: 0
          }
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(241, 245, 249, 1)' },
          ticks: { ...commonTicksConfig, precision: 0 }
        }
      }
    });

    // 1. Jobs Chart
    if (jobsChartRef.current) {
      const ctx = jobsChartRef.current.getContext('2d');
      const jobsCfg = dataLabelSettings['jobs'] || {};
      const jobsLine = jobsCfg.lineColor || '#6366f1';

      jobsChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Jobs Uploaded',
            data: chartData.jobs,
            borderColor: jobsLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: jobsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('jobs', '#6366f1'))
      });
    }

    // 2. Leads Chart
    if (leadsChartRef.current) {
      const ctx = leadsChartRef.current.getContext('2d');
      const leadsCfg = dataLabelSettings['leads'] || {};
      const leadsLine = leadsCfg.lineColor || '#10b981';

      leadsChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Leads Created',
            data: chartData.leads,
            borderColor: leadsLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: leadsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('leads', '#10b981'))
      });
    }

    // 3. Scored Jobs Chart
    if (scoredChartRef.current) {
      const ctx = scoredChartRef.current.getContext('2d');
      const scoredCfg = dataLabelSettings['scored'] || {};
      const scoredLine = scoredCfg.lineColor || '#f59e0b';

      scoredChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Jobs Scored',
            data: chartData.scored_jobs,
            borderColor: scoredLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: scoredLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('scored', '#f59e0b'))
      });
    }

    // 4. Total Tasks Chart
    if (totalTasksChartRef.current) {
      const ctx = totalTasksChartRef.current.getContext('2d');
      const totalCfg = dataLabelSettings['total_tasks'] || {};
      const totalLine = totalCfg.lineColor || '#8b5cf6';

      totalTasksChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Total Tasks Created',
            data: totalTasksData,
            borderColor: totalLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: totalLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('total_tasks', '#8b5cf6'))
      });
    }

    // 5. Tasks Status Breakdown Chart (Multi-line chart)
    if (tasksChartRef.current) {
      const ctx = tasksChartRef.current.getContext('2d');
      const taskStatuses = [
        { key: 'PENDING', label: 'Pending', color: 'rgba(245, 158, 11, 1)' },
        { key: 'STARTED', label: 'Started', color: 'rgba(59, 130, 246, 1)' },
        { key: 'IN_PROGRESS', label: 'In Progress', color: 'rgba(6, 182, 212, 1)' },
        { key: 'COMPLETED', label: 'Completed', color: 'rgba(16, 185, 129, 1)' },
        { key: 'AWAITING EMAIL', label: 'Awaiting Email', color: 'rgba(139, 92, 246, 1)' },
        { key: 'ALREADY_APPLIED', label: 'Already Applied', color: 'rgba(20, 184, 166, 1)' },
        { key: 'NOT_RELEVANT', label: 'Not Relevant', color: 'rgba(239, 68, 68, 1)' },
        { key: 'JOB_NOT_FOUND', label: 'Job Not Found', color: 'rgba(249, 115, 22, 1)' },
        { key: 'NEED_OTP', label: 'Need OTP', color: 'rgba(236, 72, 153, 1)' }
      ];

      const tbCfg = dataLabelSettings['tasks_breakdown'] || {};
      const datasets = taskStatuses.map(st => ({
        label: st.label,
        data: chartData.tasks[st.key] || Array(periodLabels.length).fill(0),
        borderColor: st.color,
        borderWidth: 2.5,
        fill: false,
        tension: 0.35,
        pointBackgroundColor: st.color,
        pointBorderColor: '#fff',
        pointBorderWidth: 1.5,
        pointRadius: 4,
        pointHoverRadius: 6,
        datalabels: tbCfg.visible ? {
          display: true,
          align: 'top',
          anchor: 'end',
          offset: 3,
          color: tbCfg.color || st.color,
          font: { family: 'Outfit, sans-serif', size: 9, weight: '700' },
          formatter: (value) => (value === 0 ? '' : value)
        } : { display: false }
      }));

      tasksChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: datasets
        },
        options: getCommonOptions(true, {})
      });
    }

    // 6. Specific Standalone Task Status Chart
    if (specificStatusChartRef.current && selectedStandaloneStatus) {
      const ctx = specificStatusChartRef.current.getContext('2d');
      const taskStatuses = [
        { key: 'PENDING', label: 'Pending', color: '#f59e0b' },
        { key: 'STARTED', label: 'Started', color: '#3b82f6' },
        { key: 'IN_PROGRESS', label: 'In Progress', color: '#06b6d4' },
        { key: 'COMPLETED', label: 'Completed', color: '#10b981' },
        { key: 'AWAITING EMAIL', label: 'Awaiting Email', color: '#8b5cf6' },
        { key: 'ALREADY_APPLIED', label: 'Already Applied', color: '#14b8a6' },
        { key: 'NOT_RELEVANT', label: 'Not Relevant', color: '#ef4444' },
        { key: 'JOB_NOT_FOUND', label: 'Job Not Found', color: '#f97316' },
        { key: 'NEED_OTP', label: 'Need OTP', color: '#ec4899' }
      ];

      const statusItem = taskStatuses.find(st => st.key === selectedStandaloneStatus) || {
        label: selectedStandaloneStatus,
        color: '#6366f1'
      };

      // User-selected line color overrides the default status color
      const ssCfg = dataLabelSettings['specific_status'] || {};
      const ssLine = ssCfg.lineColor || statusItem.color;

      specificStatusChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: `${statusItem.label} Tasks Trend`,
            data: standaloneStatusData,
            borderColor: ssLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: ssLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('specific_status', statusItem.color))
      });
    }

    // 7. Distinct Jobs Scored Chart (Special)
    if (distinctScoredChartRef.current) {
      const ctx = distinctScoredChartRef.current.getContext('2d');
      const dsCfg = dataLabelSettings['distinct_scored'] || {};
      const dsLine = dsCfg.lineColor || '#06b6d4';
      distinctScoredChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Distinct Jobs Scored',
            data: chartData.distinct_scored_jobs || [],
            borderColor: dsLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: dsLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('distinct_scored', '#06b6d4'))
      });
    }

    // 8. Distinct Jobs with Tasks Chart (Special)
    if (distinctTasksChartRef.current) {
      const ctx = distinctTasksChartRef.current.getContext('2d');
      const dtCfg = dataLabelSettings['distinct_tasks'] || {};
      const dtLine = dtCfg.lineColor || '#f43f5e';
      distinctTasksChartInstance.current = new Chart(ctx, {
        type: 'line',
        data: {
          labels: periodLabels,
          datasets: [{
            label: 'Distinct Jobs with Tasks',
            data: chartData.distinct_jobs_with_tasks || [],
            borderColor: dtLine,
            borderWidth: 3,
            fill: false,
            tension: 0.35,
            pointBackgroundColor: dtLine,
            pointBorderColor: '#fff',
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7
          }]
        },
        options: getCommonOptions(false, getSingleLineDatalabels('distinct_tasks', '#f43f5e'))
      });
    }

    // 9. Merged Graph Charts (dynamic)
    mergedGraphs.forEach(mg => {
      const canvasRef = mergedChartRefs.current[mg.id];
      if (!canvasRef || !canvasRef.current) return;
      const ctx = canvasRef.current.getContext('2d');
      const datasets = getMergedDatasets(mg.sources, mg.id);
      mergedChartInstances.current[mg.id] = new Chart(ctx, {
        type: 'line',
        data: { labels: periodLabels, datasets },
        options: getCommonOptions(true, {})
      });
    });

    return () => {
      if (jobsChartInstance.current) jobsChartInstance.current.destroy();
      if (leadsChartInstance.current) leadsChartInstance.current.destroy();
      if (scoredChartInstance.current) scoredChartInstance.current.destroy();
      if (totalTasksChartInstance.current) totalTasksChartInstance.current.destroy();
      if (tasksChartInstance.current) tasksChartInstance.current.destroy();
      if (specificStatusChartInstance.current) specificStatusChartInstance.current.destroy();
      if (distinctScoredChartInstance.current) distinctScoredChartInstance.current.destroy();
      if (distinctTasksChartInstance.current) distinctTasksChartInstance.current.destroy();
      Object.keys(mergedChartInstances.current).forEach(mid => {
        if (mergedChartInstances.current[mid]) mergedChartInstances.current[mid].destroy();
      });
      mergedChartInstances.current = {};
    };
  }, [chartData, totalTasksData, standaloneStatusData, selectedStandaloneStatus, loading, graphOrder, selectedLayout, selectedGroupBy, dataLabelSettings, mergedGraphs, getMergedDatasets]);

  // Map roles for SearchableSelect dropdown
  const searchableRoles = useMemo(() => {
    return jobRoles.map(role => ({
      value: role.id,
      label: role.name
    }));
  }, [jobRoles]);

  // Loading/Empty helpers
  const renderLoadingOverlay = () => (
    <div className="absolute inset-0 flex items-center justify-center bg-white/70">
      <div className="text-center">
        <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
        <p className="text-sm font-semibold text-slate-500">Loading chart...</p>
      </div>
    </div>
  );

  const renderEmptyState = () => (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400">
      <i className="fas fa-chart-line text-3xl mb-2 text-slate-300" />
      <span className="font-semibold text-sm">No data found</span>
    </div>
  );

  // Data label toggle + label color + line color controls per graph
  const renderDataLabelControls = (graphId) => {
    const isMerged = graphId.startsWith('merged_');
    const cfg = dataLabelSettings[graphId] || { visible: true, color: '#6366f1' };
    const hasLineColor = graphId !== 'tasks_breakdown' && !isMerged; // tasks_breakdown and merged graphs have independent lines

    const mergedGraph = isMerged ? mergedGraphs.find(m => m.id === graphId) : null;
    const mergedLines = mergedGraph ? resolveMergedLines(mergedGraph.sources) : [];

    const isVisible = cfg.visible !== false;

    return (
      <div className="flex items-center gap-1.5 flex-wrap">
        {/* Labels toggle button */}
        <button
          type="button"
          title={isVisible ? 'Hide data labels' : 'Show data labels'}
          onClick={() => updateDataLabelSetting(graphId, 'visible', !isVisible)}
          className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border transition-all cursor-pointer ${
            isVisible
              ? 'bg-indigo-50 border-indigo-200 text-indigo-700 hover:bg-indigo-100'
              : 'bg-slate-100 border-slate-200 text-slate-400 hover:bg-slate-200'
          }`}
        >
          <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 10V5a2 2 0 012-2z" />
          </svg>
          {isVisible ? 'Labels On' : 'Labels Off'}
        </button>

        {/* Label color picker – only when labels visible */}
        {isVisible && (
          <label
            title="Label text color"
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
          >
            <span
              className="inline-block w-3 h-3 rounded-full border border-slate-300"
              style={{ background: cfg.color || '#6366f1' }}
            />
            <span className="text-slate-500">Label</span>
            <input
              type="color"
              value={cfg.color || '#6366f1'}
              onChange={(e) => updateDataLabelSetting(graphId, 'color', e.target.value)}
              className="sr-only"
            />
          </label>
        )}

        {/* Line color picker – only for single-line charts */}
        {hasLineColor && (
          <label
            title="Graph line color"
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
          >
            <span
              className="inline-block w-3 h-3 rounded-sm border border-slate-300"
              style={{ background: cfg.lineColor || '#6366f1' }}
            />
            <span className="text-slate-500">Line</span>
            <input
              type="color"
              value={cfg.lineColor || '#6366f1'}
              onChange={(e) => updateDataLabelSetting(graphId, 'lineColor', e.target.value)}
              className="sr-only"
            />
          </label>
        )}

        {/* Line color pickers for merged graphs */}
        {isMerged && mergedLines.map(line => {
          const currentColor = cfg.lineColors?.[line.id] || dataLabelSettings[line.id]?.lineColor || line.defaultColor;
          return (
            <label
              key={line.id}
              title={`Graph line color for ${line.label}`}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-slate-200 bg-slate-50 hover:bg-slate-100 cursor-pointer transition-all"
            >
              <span
                className="inline-block w-3 h-3 rounded-sm border border-slate-300"
                style={{ background: currentColor }}
              />
              <span className="text-slate-500 max-w-[80px] truncate">{line.label}</span>
              <input
                type="color"
                value={currentColor}
                onChange={(e) => {
                  const updatedLineColors = {
                    ...(cfg.lineColors || {}),
                    [line.id]: e.target.value
                  };
                  updateDataLabelSetting(graphId, 'lineColors', updatedLineColors);
                }}
                className="sr-only"
              />
            </label>
          );
        })}
      </div>
    );
  };

  // Position swap arrow controls
  const renderOrderControls = (id, index) => {
    const isGrid = selectedLayout === 'grid';

    return (
      <div className="flex items-center gap-1.5 bg-slate-100 p-1 rounded-lg border border-slate-200">
        <button
          onClick={() => moveGraph(id, 'up')}
          disabled={index === 0}
          className="p-1 hover:bg-slate-200/80 disabled:opacity-30 disabled:hover:bg-transparent rounded text-slate-800 hover:text-black transition-all cursor-pointer active:scale-95 flex items-center justify-center w-6 h-6"
          title={isGrid ? "Move Left" : "Move Up"}
        >
          {isGrid ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
            </svg>
          )}
        </button>
        <button
          onClick={() => moveGraph(id, 'down')}
          disabled={index === graphOrder.length - 1}
          className="p-1 hover:bg-slate-200/80 disabled:opacity-30 disabled:hover:bg-transparent rounded text-slate-800 hover:text-black transition-all cursor-pointer active:scale-95 flex items-center justify-center w-6 h-6"
          title={isGrid ? "Move Right" : "Move Down"}
        >
          {isGrid ? (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          ) : (
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  // Render individual card wrappers based on key identifier
  const renderChartCard = (id, index) => {
    const isChartLoading = loading;
    const isChartEmpty = (chartData.periods || []).length === 0;

    if (id === 'jobs') {
      return (
        <div key="jobs" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Job Upload Trends</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> frequency of matching jobs uploaded to the system</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('jobs')}
              {renderOrderControls('jobs', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={jobsChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'leads') {
      return (
        <div key="leads" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Leads Created Trends</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> volume of new leads registered in the system</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('leads')}
              {renderOrderControls('leads', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={leadsChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'scored') {
      return (
        <div key="scored" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Scored Jobs Trends</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> count of matching jobs scored by AI models</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('scored')}
              {renderOrderControls('scored', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={scoredChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'total_tasks') {
      return (
        <div key="total_tasks" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Total Tasks Created</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> volume of tasks created in the system</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('total_tasks')}
              {renderOrderControls('total_tasks', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={totalTasksChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'tasks_breakdown') {
      return (
        <div key="tasks_breakdown" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">Tasks Status Breakdown</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> task volume breakdown. Click legend items below to toggle individual lines.</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('tasks_breakdown')}
              {renderOrderControls('tasks_breakdown', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={tasksChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'specific_status') {
      const taskLabels = {
        'PENDING': 'Pending',
        'STARTED': 'Started',
        'IN_PROGRESS': 'In Progress',
        'COMPLETED': 'Completed',
        'AWAITING EMAIL': 'Awaiting Email',
        'ALREADY_APPLIED': 'Already Applied',
        'NOT_RELEVANT': 'Not Relevant',
        'JOB_NOT_FOUND': 'Job Not Found',
        'NEED_OTP': 'Need OTP'
      };
      const label = taskLabels[selectedStandaloneStatus] || selectedStandaloneStatus;

      return (
        <div key="specific_status" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-800">{label} Tasks Trend</h3>
              <p className="text-xs font-medium text-slate-400 mt-1"><span className="capitalize">{selectedGroupBy}</span> volume of standalone {label.toLowerCase()} tasks</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('specific_status')}
              {renderOrderControls('specific_status', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={specificStatusChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'distinct_scored') {
      return (
        <div key="distinct_scored" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full" style={{borderTop: '3px solid #06b6d4'}}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-cyan-50 text-cyan-700 border border-cyan-200">⚡ Special</span>
                <h3 className="text-lg font-bold text-slate-800">Distinct Jobs Scored</h3>
              </div>
              <p className="text-xs font-medium text-slate-400"><span className="capitalize">{selectedGroupBy}</span> count of unique job IDs scored (regardless of how many leads)</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('distinct_scored')}
              {renderOrderControls('distinct_scored', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={distinctScoredChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id === 'distinct_tasks') {
      return (
        <div key="distinct_tasks" className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full" style={{borderTop: '3px solid #f43f5e'}}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-rose-50 text-rose-700 border border-rose-200">⚡ Special</span>
                <h3 className="text-lg font-bold text-slate-800">Distinct Jobs with Tasks</h3>
              </div>
              <p className="text-xs font-medium text-slate-400"><span className="capitalize">{selectedGroupBy}</span> count of unique job IDs that have at least one task created</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls('distinct_tasks')}
              {renderOrderControls('distinct_tasks', index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={distinctTasksChartRef}></canvas>
          </div>
        </div>
      );
    }

    if (id.startsWith('merged_')) {
      const mg = mergedGraphs.find(m => m.id === id);
      if (!mg) return null;
      // Ensure a stable canvas ref object exists for this merged graph
      if (!mergedChartRefs.current[id]) {
        mergedChartRefs.current[id] = { current: null };
      }
      const canvasRef = mergedChartRefs.current[id];
      return (
        <div key={id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 flex flex-col h-full" style={{borderTop: '3px solid #6366f1'}}>
          <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">🔗 Merged</span>
                <h3 className="text-lg font-bold text-slate-800">{mg.name}</h3>
              </div>
              <p className="text-xs font-medium text-slate-400">Combined view of: {mg.sources.join(', ')}</p>
            </div>
            <div className="flex items-center gap-2">
              {renderDataLabelControls(id)}
              {renderOrderControls(id, index)}
              {/* Edit merged graph */}
              <button
                type="button"
                title="Edit merged graph"
                onClick={() => {
                  setEditingMergeId(id);
                  setMergeModalName(mg.name);
                  setMergeModalSources(mg.sources);
                  setShowMergeModal(true);
                }}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-slate-200 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 hover:text-indigo-700 text-slate-500 transition-all cursor-pointer"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.25 2.25 0 113.182 3.182L7.5 19.213l-4.5 1.318 1.318-4.5 12.544-12.544z" />
                </svg>
                Edit
              </button>
              {/* Delete merged graph */}
              <button
                type="button"
                title="Delete merged graph"
                onClick={() => {
                  setMergedGraphs(prev => prev.filter(m => m.id !== id));
                  setGraphOrder(g => g.filter(gid => gid !== id));
                  if (mergedChartInstances.current[id]) {
                    mergedChartInstances.current[id].destroy();
                    delete mergedChartInstances.current[id];
                  }
                  delete mergedChartRefs.current[id];
                }}
                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100 transition-all cursor-pointer"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Delete
              </button>
              {renderOrderControls(id, index)}
            </div>
          </div>
          <div className="relative flex-1 min-h-[320px] w-full">
            {isChartLoading ? renderLoadingOverlay() : isChartEmpty ? renderEmptyState() : null}
            <canvas ref={el => { canvasRef.current = el; }}></canvas>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-8 animate-fadeIn">
      {/* Header and Subtitle */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-100 pb-6 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
            Trends Dashboard
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-2">
            Analyze historical monthly job uploads, leads count, scored matches, and task completion.
          </p>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <div className="flex items-center gap-2 mb-8 bg-slate-100/60 p-1.5 rounded-xl border border-slate-200/50 w-fit select-none">
        <Link
          to="/scoring-queue-dashboard"
          className="px-4 py-2 text-slate-600 hover:text-slate-800 rounded-lg text-sm font-semibold flex items-center gap-1.5 transition-all hover:bg-slate-200/40"
        >
          <i className="fas fa-list-ul text-xs" />
          Summary Dashboard
        </Link>
        <Link
          to="/trends-dashboard"
          className="px-4 py-2 bg-white text-indigo-700 shadow-sm border border-slate-200/40 rounded-lg text-sm font-bold flex items-center gap-1.5 transition-all"
        >
          <i className="fas fa-chart-line text-xs" />
          Trends Dashboard
        </Link>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8">
        {/* Feature buttons row */}
        <div className="flex items-center gap-3 mb-5 pb-5 border-b border-slate-100 flex-wrap">
          <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Features</span>
          {/* Special Graphs toggle */}
          <button
            type="button"
            onClick={() => setShowSpecialGraphs(v => !v)}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${
              showSpecialGraphs
                ? 'bg-cyan-500 text-white border-cyan-500 shadow-sm shadow-cyan-200'
                : 'bg-white text-slate-600 border-slate-200 hover:border-cyan-400 hover:text-cyan-600'
            }`}
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .292 2.712-1.104 2.78l-1.982.094" />
            </svg>
            {showSpecialGraphs ? 'Hide Special Graphs' : 'Special Graphs'}
          </button>

          {/* Merge Graphs button */}
          <button
            type="button"
            onClick={() => {
              setEditingMergeId(null);
              setMergeModalName('');
              setMergeModalSources([]);
              setShowMergeModal(true);
            }}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-bold border bg-white text-slate-600 border-slate-200 hover:border-indigo-400 hover:text-indigo-600 transition-all cursor-pointer"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 3M21 7.5H7.5" />
            </svg>
            Merge Graphs
            {mergedGraphs.length > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[9px] font-black">{mergedGraphs.length}</span>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-end">

          {/* Preset Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Date Range</label>
            <select
              value={selectedRangePreset}
              onChange={(e) => setSelectedRangePreset(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-slate-700 transition-all cursor-pointer"
            >
              <option value="all_time">All Time</option>
              <option value="last_30_days">Last 30 Days</option>
              <option value="last_3_months">Last 3 Months</option>
              <option value="last_6_months">Last 6 Months</option>
              <option value="last_12_months">Last 12 Months</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>

          {/* Inline Custom Inputs */}
          {selectedRangePreset === 'custom' ? (
            <div className="lg:col-span-2 grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Start Date</label>
                <input
                  type="date"
                  value={customStartDate || oldestSystemDate}
                  min={oldestSystemDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-slate-700 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">End Date</label>
                <input
                  type="date"
                  value={customEndDate || new Date().toISOString().split('T')[0]}
                  min={customStartDate || oldestSystemDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-slate-700 transition-all"
                />
              </div>
            </div>
          ) : (
            <div className="hidden lg:block lg:col-span-2" />
          )}

          {/* Group By Filter */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Group By</label>
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200/50 w-full select-none">
              {['yearly', 'quarterly', 'monthly', 'daily'].map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSelectedGroupBy(mode)}
                  className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold capitalize transition-all cursor-pointer ${selectedGroupBy === mode
                      ? 'bg-white text-indigo-700 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                    }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          {/* Layout Selector */}
          <div>
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Layout View</label>
            <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200/50 w-full select-none">
              <button
                type="button"
                onClick={() => setSelectedLayout('list')}
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${selectedLayout === 'list'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <i className="fas fa-align-justify mr-1" /> List
              </button>
              <button
                type="button"
                onClick={() => setSelectedLayout('grid')}
                className={`flex-1 py-1.5 rounded-lg text-[10px] font-bold transition-all cursor-pointer ${selectedLayout === 'grid'
                    ? 'bg-white text-indigo-700 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700'
                  }`}
              >
                <i className="fas fa-th mr-1" /> Grid
              </button>
            </div>
          </div>

          {/* Target Job Role Filter */}
          <div className="lg:col-span-3">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Target Job Role</label>
            <SearchableSelect
              options={searchableRoles}
              value={selectedRole}
              onChange={setSelectedRole}
              placeholder="All Job Roles"
            />
          </div>

          {/* Standalone Status Selector dropdown */}
          <div className="lg:col-span-2">
            <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Compare Standalone Task Status</label>
            <select
              value={selectedStandaloneStatus}
              onChange={(e) => setSelectedStandaloneStatus(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-slate-700 transition-all cursor-pointer"
            >
              <option value="">None (Hide standalone status graph)</option>
              <option value="PENDING">Pending</option>
              <option value="STARTED">Started</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="COMPLETED">Completed</option>
              <option value="AWAITING EMAIL">Awaiting Email</option>
              <option value="ALREADY_APPLIED">Already Applied</option>
              <option value="NOT_RELEVANT">Not Relevant</option>
              <option value="JOB_NOT_FOUND">Job Not Found</option>
              <option value="NEED_OTP">Need OTP</option>
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border-l-4 border-rose-500 p-4 rounded-r-lg mb-8 shadow-sm">
          <span className="text-rose-700 font-medium">{error}</span>
        </div>
      )}

      {/* Dynamic Layout Wrapper */}
      <div className={selectedLayout === 'grid' ? "grid grid-cols-1 lg:grid-cols-2 gap-8" : "flex flex-col gap-8"}>
        {graphOrder.map((id, index) => renderChartCard(id, index))}
      </div>

      {/* Merge Graphs Modal */}
      {showMergeModal && (() => {
        // All available graph options for selection
        const BASE_GRAPH_OPTIONS = [
          { id: 'jobs',            label: 'Job Upload Trends' },
          { id: 'leads',           label: 'Leads Created Trends' },
          { id: 'scored',          label: 'Scored Jobs Trends' },
          { id: 'total_tasks',     label: 'Total Tasks Created' },
          { id: 'tasks_breakdown', label: 'Tasks Status Breakdown' },
          { id: 'distinct_scored', label: 'Distinct Jobs Scored ⚡' },
          { id: 'distinct_tasks',  label: 'Distinct Jobs with Tasks ⚡' },
          ...(selectedStandaloneStatus ? [{ id: 'specific_status', label: `${selectedStandaloneStatus} Standalone Status` }] : []),
        ];
        // Add existing merged graphs as sources (exclude the one being edited)
        const MERGED_OPTIONS = mergedGraphs
          .filter(mg => mg.id !== editingMergeId)
          .map(mg => ({ id: mg.id, label: `${mg.name} 🔗` }));
        const ALL_OPTIONS = [...BASE_GRAPH_OPTIONS, ...MERGED_OPTIONS];

        const toggleSource = (srcId) => {
          setMergeModalSources(prev =>
            prev.includes(srcId) ? prev.filter(s => s !== srcId) : [...prev, srcId]
          );
        };

        const handleSave = () => {
          if (!mergeModalName.trim() || mergeModalSources.length === 0) return;
          if (editingMergeId) {
            // Update existing merged graph
            setMergedGraphs(prev => prev.map(mg =>
              mg.id === editingMergeId ? { ...mg, name: mergeModalName.trim(), sources: mergeModalSources } : mg
            ));
          } else {
            // Create new merged graph
            const newId = `merged_${Date.now()}`;
            mergedChartRefs.current[newId] = { current: null };
            setMergedGraphs(prev => [...prev, { id: newId, name: mergeModalName.trim(), sources: mergeModalSources }]);
            setGraphOrder(g => [newId, ...g]);
          }
          setShowMergeModal(false);
          setMergeModalName('');
          setMergeModalSources([]);
          setEditingMergeId(null);
        };

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
              onClick={() => setShowMergeModal(false)}
            />
            {/* Modal Panel */}
            <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-lg mx-4 z-10 overflow-hidden">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-indigo-50 to-white">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 3M21 7.5H7.5" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base font-bold text-slate-800">{editingMergeId ? 'Edit Merged Graph' : 'Create Merged Graph'}</h2>
                    <p className="text-xs text-slate-400 font-medium">Combine multiple graphs into one multi-line chart</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMergeModal(false)}
                  className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-all cursor-pointer"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-5">
                {/* Name Input */}
                <div className="mb-5">
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Graph Name</label>
                  <input
                    type="text"
                    placeholder="e.g. Jobs vs Leads Overview"
                    value={mergeModalName}
                    onChange={e => setMergeModalName(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 focus:bg-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-sm font-semibold text-slate-700 transition-all"
                  />
                </div>

                {/* Source Selector */}
                <div>
                  <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                    Select Source Graphs
                    {mergeModalSources.length > 0 && (
                      <span className="ml-2 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-black normal-case">{mergeModalSources.length} selected</span>
                    )}
                  </label>
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                    {ALL_OPTIONS.map(opt => {
                      const isChecked = mergeModalSources.includes(opt.id);
                      return (
                        <label
                          key={opt.id}
                          className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border cursor-pointer transition-all select-none ${
                            isChecked
                              ? 'bg-indigo-50 border-indigo-200 text-indigo-800'
                              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSource(opt.id)}
                            className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <span className="text-sm font-semibold">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Modal Footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-100 bg-slate-50">
                <button
                  type="button"
                  onClick={() => setShowMergeModal(false)}
                  className="px-4 py-2 rounded-xl text-sm font-bold text-slate-600 bg-white border border-slate-200 hover:bg-slate-100 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!mergeModalName.trim() || mergeModalSources.length === 0}
                  onClick={handleSave}
                  className="px-5 py-2 rounded-xl text-sm font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer shadow-sm"
                >
                  {editingMergeId ? 'Save Changes' : 'Create Graph'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
